import { FairPaySurveyResponseContext } from "contexts/FairPaySurveyResponse";
import { useContext } from "react";

import {
  ErrorField,
  FairPaySurveyFollowerCount,
} from "features/FairPaySurvey/schemas";
import { makeDeepCopy } from "utils/updateLocalState";

import FairPaySelect from "../components/FairPaySelect";
import FairPayTextField from "../components/FairPayTextField";

interface Props {
  error: ErrorField | null;
  setError: React.Dispatch<React.SetStateAction<ErrorField | null>>;
}

const FairPaySocial = ({ error, setError }: Props) => {
  const { fairPaySurveyResponse, setFairPaySurveyResponse } = useContext(
    FairPaySurveyResponseContext,
  );

  const sanitizeInput = (str: string | undefined) => {
    const inputStr = str?.trim();
    if (!inputStr) {
      return "";
    }
    const regex =
      /(?:instagram\.com|tiktok\.com|youtube\.com)\/(?:_u\/|@)?([^/?\s]*)(\/|\?|$)/;
    const matches = regex.exec(inputStr);
    const extractedUsername = matches && matches.length > 1 ? matches[1] : null;
    if (extractedUsername !== null) return extractedUsername;
    if (inputStr.startsWith("@")) {
      return inputStr.slice(1);
    }

    const urlRegex = /^(?:https?:\/\/)?(?:www\.)?([^/?\s]*)/;
    const urlMatches = urlRegex.exec(inputStr);
    const extractedUrl =
      urlMatches && urlMatches.length > 1 ? urlMatches[1] : null;
    if (extractedUrl !== null) return extractedUrl;
    return inputStr;
  };

  const onBlur = (name: string, value?: string | number) => {
    const sanitizedValue = sanitizeInput(value?.toString());
    if (!isValidUsername(sanitizedValue)) {
      setError({
        name,
        message:
          "Must contain only letters, numbers, underscores, hyphens or periods",
      });
      return;
    }
    setError(null);
    if (sanitizedValue) {
      setFairPaySurveyResponse((prev) => {
        const copy = makeDeepCopy(prev) || {};
        copy[name] = sanitizedValue;
        return copy;
      });
    }
  };

  const isValidUsername = (str: string | undefined) => {
    if (!str || str === "") {
      return true;
    }
    const regex = /^[a-zA-Z0-9_.-]+$/;
    return regex.test(str);
  };

  return (
    <>
      <FairPayTextField
        placeholder="First Name"
        name={"firstName"}
        value={fairPaySurveyResponse?.firstName}
        required={true}
      />
      <FairPayTextField
        placeholder="Instagram Username"
        name={"instagramUsername"}
        value={fairPaySurveyResponse?.instagramUsername}
        onBlur={onBlur}
        error={error?.name === "instagramUsername" ? error.message : undefined}
      />
      <FairPayTextField
        placeholder="TikTok Username"
        name={"tiktokUsername"}
        value={fairPaySurveyResponse?.tiktokUsername}
        onBlur={onBlur}
        error={error?.name === "tiktokUsername" ? error.message : undefined}
      />

      <FairPayTextField
        placeholder="YouTube Username"
        name={"youtubeUsername"}
        value={fairPaySurveyResponse?.youtubeUsername}
        onBlur={onBlur}
        error={error?.name === "youtubeUsername" ? error.message : undefined}
      />

      <FairPaySelect
        label={
          "Please share your follower count (highest number on a single platform)"
        }
        name="followerCount"
        options={Object.values(FairPaySurveyFollowerCount)}
        selectedOption={fairPaySurveyResponse?.followerCount}
      />
    </>
  );
};

export default FairPaySocial;
